import React from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, ArrowRight, FileText, Users, Mic, Shuffle, PhoneOff, PhoneMissed } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { OngoingCallAction, type ScriptType, type ContactListDto, type VoicemailDto } from '../types/wizard.types';
import MicrophoneSetupModal from '@ui/shared/Dialer/MicrophoneSetupModal.tsx';

interface SavedProgressPromptProps {
  savedIntent: ScriptType | null;
  savedContactList: ContactListDto | null;
  savedVoicemail: VoicemailDto | null;
  shuffleContacts: boolean;
  ongoingCallAction: OngoingCallAction;
  onContinue: () => void;
  onStartFresh: () => void;
}

export default function SavedProgressPrompt ({
  savedIntent,
  savedContactList,
  savedVoicemail,
  shuffleContacts,
  ongoingCallAction,
  onContinue,
  onStartFresh,
}: SavedProgressPromptProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="relative h-full">
      <MicrophoneSetupModal />
      <div className="mx-auto max-w-2xl">
        <Card className="p-6 rounded-2xl shadow-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/30">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
              <RotateCcw className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <CardTitle className="text-2xl">
              {t('wizard.savedProgress.title')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('wizard.savedProgress.description')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Saved Data Info */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
              {/* Intent */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  {t('wizard.savedProgress.intent')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={savedIntent?.title ?? ''}>
                  {savedIntent?.title ?? '—'}
                </span>
              </div>

              {/* Contact List */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {t('wizard.savedProgress.contactList')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={savedContactList?.title ?? ''}>
                  {savedContactList?.title ?? '—'}
                </span>
              </div>

              {/* Voicemail */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mic className="h-4 w-4" />
                  {t('wizard.savedProgress.voicemail')}
                </span>
                <span className="text-sm font-medium truncate max-w-[200px]" title={savedVoicemail?.title ?? ''}>
                  {savedVoicemail?.title ?? '—'}
                </span>
              </div>

              {/* Shuffle Contacts */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shuffle className="h-4 w-4" />
                  {t('wizard.savedProgress.shuffle')}
                </span>
                <span className="text-sm font-medium">
                  {shuffleContacts
                    ? t('wizard.savedProgress.shuffleOn')
                    : t('wizard.savedProgress.shuffleOff')
                  }
                </span>
              </div>

              {/* Ongoing Calls Behavior */}
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  {ongoingCallAction === OngoingCallAction.CUT_OFF ? (
                    <PhoneOff className="h-4 w-4" />
                  ) : (
                    <PhoneMissed className="h-4 w-4" />
                  )}
                  {t('wizard.savedProgress.ongoingCalls')}
                </span>
                <span className="text-sm font-medium">
                  {ongoingCallAction === OngoingCallAction.CUT_OFF
                    ? t('wizard.savedProgress.cutOffMode')
                    : t('wizard.savedProgress.holdMode')
                  }
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                size="lg"
                onClick={onContinue}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                {t('wizard.savedProgress.continue')}
              </Button>

              <Button
                variant="outline"
                className="flex-1 border border-border text-foreground hover:bg-muted"
                size="lg"
                onClick={onStartFresh}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('wizard.savedProgress.startFresh')}
              </Button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              {t('wizard.savedProgress.hint')}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
